import { System } from '@ecs/System';
import { Position } from '@components/Position';
import { RailgunWeapon } from '@components/RailgunWeapon';
import { SniperAI } from '@components/SniperAI';
import { AI } from '@components/AI';
import { Tag } from '@components/Tag';
import { WeaponUtils } from '@utils/WeaponUtils';
import { Collider } from '@components/Collider';
import { BulletTrail } from '@components/BulletTrail';
import { SoundManager, SoundType } from '@audio/SoundManager';
import { Effects } from '@/effects/Effects';
import { addCameraTrauma } from '@utils/CameraUtils';

export class RailgunWeaponSystem extends System {
  private soundManager = SoundManager.getInstance();
  private canvas!: HTMLCanvasElement;

  setCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
  }
  update(_deltaTime: number): void {
    const currentTime = Date.now();
    const railgunners = this.getEntitiesWithComponents(Position, RailgunWeapon, Tag);

    for (const entity of railgunners) {
      const tag = entity.getComponent(Tag)!;
      if (tag.value !== 'enemy') continue;

      const position = entity.getComponent(Position)!;
      const weapon = entity.getComponent(RailgunWeapon)!;
      const sniperAI = entity.getComponent(SniperAI);
      const ai = entity.getComponent(AI);

      // Only shoot if in position, within viewport and with a player in range.
      // Otherwise abort any charge in progress - a stuck charge freezes the sniper.
      const nearestPlayer = WeaponUtils.findNearestTarget(this.world, position, weapon.range, 'player');
      if (!sniperAI || !sniperAI.isInPosition ||
          !WeaponUtils.isInViewport(position, this.canvas) || !nearestPlayer) {
        weapon.isCharging = false;
        continue;
      }
      
      // Check if ready to shoot
      if (currentTime - weapon.lastShot < weapon.cooldown) continue;

      // Track the target while charging so the aim telegraph follows it
      weapon.aimAngle = Math.atan2(
        nearestPlayer.position.y - position.y,
        nearestPlayer.position.x - position.x
      );

      // Start charging if not already
      if (!weapon.isCharging) {
        weapon.isCharging = true;
        weapon.chargeStartTime = currentTime;
        
        // Create charging effect
        this.createChargingEffect(position);
        
        // Play charging sound
        this.soundManager.playSound(SoundType.RAILGUN_CHARGE, 0.6);
      }

      // Check if charge is complete
      if (weapon.isCharging && currentTime - weapon.chargeStartTime >= weapon.chargeTime) {
        // Fire!
        this.createRailgunBullet(position, nearestPlayer.position, weapon);

        // Create enhanced muzzle flash for railgun
        this.createRailgunMuzzleFlash(position, weapon.aimAngle);
        
        // Play railgun fire sound
        this.soundManager.playSound(SoundType.RAILGUN_FIRE, 0.8);

        // Reset weapon state
        weapon.lastShot = currentTime;
        weapon.isCharging = false;
        
        // Update AI shot time
        if (ai) {
          ai.lastShot = currentTime;
        }
      }
    }
  }

  private createChargingEffect(position: Position): void {
    // Create multiple rings of particles that move inward
    const particlesPerRing = 8;
    
    for (let i = 0; i < particlesPerRing; i++) {
      const angle = (Math.PI * 2 * i) / particlesPerRing;
      const distance = 40;
      
      // Outer ring particles
      const particle1 = new Entity()
        .addComponent(new Position(
          position.x + Math.cos(angle) * distance,
          position.y + Math.sin(angle) * distance
        ))
        .addComponent(new Velocity(
          Math.atan2(position.y - (position.y + Math.sin(angle) * distance), 
                     position.x - (position.x + Math.cos(angle) * distance)),
          1.5
        ))
        .addComponent(new Renderable('particle', '#ff00ff', 3))
        .addComponent(new Lifetime(30, 30))
        .addComponent(new Tag('particle'));

      this.world.addEntity(particle1);
      
      // Inner ring particles with slight offset
      const innerAngle = angle + Math.PI / particlesPerRing;
      const innerDistance = 25;
      
      const particle2 = new Entity()
        .addComponent(new Position(
          position.x + Math.cos(innerAngle) * innerDistance,
          position.y + Math.sin(innerAngle) * innerDistance
        ))
        .addComponent(new Velocity(
          Math.atan2(position.y - (position.y + Math.sin(innerAngle) * innerDistance), 
                     position.x - (position.x + Math.cos(innerAngle) * innerDistance)),
          1
        ))
        .addComponent(new Renderable('circle', '#ff88ff', 2))
        .addComponent(new Lifetime(25, 25))
        .addComponent(new Tag('particle'));

      this.world.addEntity(particle2);
    }
  }

  private createRailgunMuzzleFlash(position: Position, angle: number): void {
    const x = position.x + Math.cos(angle) * 20;
    const y = position.y + Math.sin(angle) * 20;
    Effects.muzzleFlash(this.world, x, y, angle, '#ff66ff', 3);
    Effects.shieldRipple(this.world, x, y, angle, '#ff00ff');
    addCameraTrauma(this.world, 0.08);
  }

  private createRailgunBullet(from: Position, to: Position, weapon: RailgunWeapon): void {
    const angle = Math.atan2(to.y - from.y, to.x - from.x);
    
    const bullet = new Entity()
      .addComponent(new Position(
        from.x + Math.cos(angle) * 20,
        from.y + Math.sin(angle) * 20
      ))
      .addComponent(new Velocity(angle, weapon.bulletSpeed))
      .addComponent(new Renderable('bolt', weapon.bulletColor, 5, '#ff88ff', 2, weapon.bulletColor, 12)) // Larger bullet
      .addComponent(new Lifetime(200, 200)) // Longer lifetime
      .addComponent(new Collider(10, 'enemyBullet', ['player'])) // Larger hitbox
      .addComponent(new Tag('bullet'))
      .addComponent(new BulletTrail(45))
      .addComponent(new Damage(weapon.damage));

    this.world.addEntity(bullet);
  }
}

// Need to import these for effects
import { Entity } from '@ecs/Entity';
import { Velocity } from '@components/Velocity';
import { Renderable } from '@components/Renderable';
import { Lifetime } from '@components/Lifetime';
import { Damage } from '@components/Damage';